import { SecondaryButton } from "common";
import { isNil, omit, pick } from "lodash";
import React, { useState } from "react";
import { MutateOptions, useMutation } from "react-query";
import { LineItemObject, LineItemType, Purchase, PurchaseItem, RewardCodeObject } from "types";
import { PromptModal } from "../Common/PromptModal";
import { membershipRestrictionFields } from "../Product/Membership/MembershipRestrictions/MembershipRestrictions";
import { ApplyCouponInline, CreatePurchaseApplyCouponModal } from "../PurchasesMenu/ApplyCouponModal";
import { lineItemsToIgnoreWhenApplyingCode } from "../PurchasesMenu/ApplySubmitCodeHelper";
import { allowedKeysToMapPurchaseToStore } from "./helpers/PurchaseCreateHelpers";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { api } from "@/api/api";
import { usePreviousUpdated } from "@/hooks/usePrevious";
import { getCouponLineItems, getMembershipCodeLineItems, isExperiencePurchaseItem } from "@/models/PurchaseItem";
import {
    CodeRestrictionModalState,
    CreatePurchaseItemLineItem,
    DemographicLineItem,
    useCreatePurchaseStore,
} from "@/stores/createPurchaseStore";

type GetPayloadType = {
    previousPurchase: Purchase;
    codeType?: RewardCodeObject;
    modifyCodes: (
        variables: any,
        options?: MutateOptions<Purchase | undefined> | undefined,
    ) => Promise<Purchase | undefined>;
};

const getPurchaseItemPayload = (previousPurchase: Purchase, item: PurchaseItem, codeType?: RewardCodeObject) => {
    if (codeType === RewardCodeObject.MEMBERSHIP) {
        return {
            id: getMembershipCodeLineItems(item)[0]?.id ?? null,
            object: LineItemObject.DISCOUNT_CODE,
            path: `/items/${item.id}/lineItems`,
        };
    }

    const couponDiscountLineItem = getCouponLineItems(item)[0] ?? null;
    if (couponDiscountLineItem) {
        return {
            id: couponDiscountLineItem?.id,
            object: LineItemObject.COUPON,
            path: `/items/${item.id}/lineItems`,
        };
    }

    const paymentLineItem = previousPurchase?.lineItems.find((lineItem) => lineItem.type === LineItemType.PAYMENT);
    if (paymentLineItem?.id) {
        return {
            id: paymentLineItem.id,
            object: LineItemObject.PAYMENT,
            path: "/lineItems",
        };
    }

    return null;
};

const getPayload = async ({ modifyCodes, previousPurchase, codeType }: GetPayloadType) => {
    const data = await modifyCodes({
        ops: previousPurchase.items
            .map((item) => {
                const purchaseItemPayload = getPurchaseItemPayload(previousPurchase, item, codeType);
                if (!purchaseItemPayload) {
                    return null;
                }

                return {
                    path: purchaseItemPayload.path,
                    op: "removeLineItem",
                    data: omit(purchaseItemPayload, "path"),
                };
            })
            .filter((opsData) => !isNil(opsData)),
    });

    return data;
};

export const CreatePurchaseApplyCode = React.memo(() => {
    // This state is used to disable the cancel and submit buttons in the Coupon Restrictions modal
    // so that the user cannot request multiple actions at the same time.
    const [isLoading, setIsLoading] = useState(false);
    const [showApplyCodeModal, setShowApplyCodeModal] = useState(false);

    const shouldShowCodeRestrictions = useCreatePurchaseStore((state) => state.shouldShowCodeRestrictions);
    const organizer = useCreatePurchaseStore((state) => state.organizer);

    const previousOrganizer = usePreviousUpdated(organizer);
    const isMist = usePurchaseResponseStore((state) => (state.purchase.items?.length ?? 0) > 1);

    const { mutateAsync: modifyCodes } = useMutation(async (payload: any) => {
        try {
            const purchaseId = usePurchaseResponseStore.getState().purchase.id;
            const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${purchaseId}`, payload);

            return updatedPurchase;
        } catch {
            console.log("Error saving Codes");
        }
    });

    const handleApiValidationClose = () => {
        const { previousPurchase } = useCreatePurchaseStore.getState();
        if (previousPurchase) {
            const isMembershipPassHolderRestrictionError =
                shouldShowCodeRestrictions.errorCode === "equals_filter_not_met" &&
                shouldShowCodeRestrictions.errorField === membershipRestrictionFields.organizer;

            useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                const clonedItems = draft.items.map((item, index) => {
                    const previousItem = previousPurchase.items[index];
                    const filteredPurchaseLineItems = previousItem.lineItems
                        .filter((lineItem) => !lineItemsToIgnoreWhenApplyingCode.includes(lineItem.type))
                        .map((lineItem) => pick(lineItem, allowedKeysToMapPurchaseToStore));

                    item.lineItems = filteredPurchaseLineItems as CreatePurchaseItemLineItem[];
                    item.quantity = previousItem.quantity;
                    if (isExperiencePurchaseItem(previousItem)) {
                        const demographicLineItems = previousItem.lineItems.filter(
                            (lineItem) => lineItem.type === LineItemType.DEMOGRAPHIC,
                        );
                        item.demographicLineItems = demographicLineItems.map((lineItem) => ({
                            quantity: lineItem.quantity,
                            template: lineItem.template,
                            name: lineItem.name,
                            type: lineItem.type,
                        })) as DemographicLineItem[];
                        item.arrivalDate = previousItem.arrivalDate;
                        item.arrivalDateTime = previousItem.arrivalDateTime;
                        item.arrivalTime = previousItem.arrivalTime;
                    }

                    return { ...item };
                });

                if (isMembershipPassHolderRestrictionError) {
                    draft.customerEmail = previousPurchase.customerEmail;
                    draft.customerName = previousPurchase.customerName;
                    draft.phone = previousPurchase.phone;
                }

                draft.items = clonedItems;
            });

            useCreatePurchaseStore.setState({
                preventQuantitySync: true,
                ...(isMembershipPassHolderRestrictionError ? { organizer: previousOrganizer } : {}),
            });
        }

        useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(true);

        setIsLoading(true);
        // We use a timeout here so that the purchase has time to update before the request.
        // Otherwise, this causes a bug when the user quickly clicks the cancel button and the purchase that is not actual is sent as a payload.
        setTimeout(() => {
            useCreatePurchaseStore.setState({
                shouldShowCodeRestrictions: {
                    isOpen: false,
                    validationLevel: "",
                    message: "",
                },
            });
            setIsLoading(false);
            useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(false);
        }, 900);
    };

    const handleApiValidationSubmit = async () => {
        const { previousPurchase } = useCreatePurchaseStore.getState();
        if (previousPurchase) {
            if (shouldShowCodeRestrictions.isOpen && isSupportedCodeRestriction(shouldShowCodeRestrictions)) {
                useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(true);

                const data = await getPayload({
                    modifyCodes,
                    previousPurchase,
                    codeType: shouldShowCodeRestrictions.codeType,
                });

                if (data) {
                    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                        for (const [index, item] of data.items.entries()) {
                            const lineItems = item.lineItems
                                .filter((lineItem) => !lineItemsToIgnoreWhenApplyingCode.includes(lineItem.type))
                                .map((lineItem) => pick(lineItem, allowedKeysToMapPurchaseToStore));

                            draft.items[index].lineItems = lineItems as CreatePurchaseItemLineItem[];
                        }
                    });
                    useCreatePurchaseStore.setState({
                        balance: data.balance,
                        dueNow: data.dueNow,
                        amount: data.amount,
                    });
                }
            }

            setIsLoading(true);
            // We use a timeout here so that the purchase has time to update before the request.
            // Otherwise, this causes a bug when the user quickly clicks the cancel button and the purchase that is not actual is sent as a payload.
            setTimeout(() => {
                useCreatePurchaseStore.setState({
                    shouldShowCodeRestrictions: {
                        isOpen: false,
                        validationLevel: "info",
                        message: "",
                    },
                });
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    const { activeItemIndex } = useCreatePurchaseStore.getState();
                    const item = draft.items[activeItemIndex];
                    const couponDiscountLineItem = item.lineItems.find(
                        ({ type }) => type === LineItemType.COUPON_DISCOUNT,
                    );
                    if (couponDiscountLineItem && "amount" in couponDiscountLineItem) {
                        couponDiscountLineItem.amount = 0;
                    }

                    const lineItems = item.lineItems.filter((lineItem) => lineItem.id !== couponDiscountLineItem?.id);
                    draft.items[activeItemIndex].lineItems = [...lineItems, couponDiscountLineItem].filter(
                        Boolean,
                    ) as CreatePurchaseItemLineItem[];
                });
                setIsLoading(false);
                useCreatePurchaseStore.getState().actions.setIsFetchingPreviewPurchase(false);
            }, 900);
        }
    };

    return (
        <>
            {isMist ? (
                <SecondaryButton className="focus:ring-0" onClick={() => setShowApplyCodeModal(true)}>
                    Add Code
                </SecondaryButton>
            ) : (
                <ApplyCouponInline />
            )}
            <PromptModal
                title={
                    shouldShowCodeRestrictions.codeType === RewardCodeObject.MEMBERSHIP
                        ? "Membership Restrictions"
                        : "Coupon Restrictions"
                }
                isOpen={shouldShowCodeRestrictions.isOpen}
                isLoading={isLoading}
                onClose={handleApiValidationClose}
                onConfirm={handleApiValidationSubmit}
            >
                {shouldShowCodeRestrictions.message}
            </PromptModal>
            {showApplyCodeModal && (
                <CreatePurchaseApplyCouponModal
                    isOpen={showApplyCodeModal}
                    onClose={() => setShowApplyCodeModal(false)}
                />
            )}
        </>
    );
});

const isSupportedCodeRestriction = (restriction: CodeRestrictionModalState) => {
    if (restriction.errorCode === "in_schedule_filter_not_met") {
        return true;
    }

    if (restriction.errorCode === "equals_filter_not_met") {
        if (
            restriction.errorField &&
            [membershipRestrictionFields.organizer, "privacy"].includes(restriction.errorField)
        ) {
            return true;
        }

        return true;
    }

    return false;
};

CreatePurchaseApplyCode.displayName = "CreatePurchaseApplyCode";
